/**
 * Customer chat simulator (hidden SystemAdmin-only /chat route).
 *
 * Lets a SystemAdmin act as a WhatsApp customer to drive and demo the bot
 * without a real device: messages are sent through the authenticated /api/dev
 * endpoints in "simulate" mode (the bot processes the funnel and logs replies,
 * but nothing is delivered over WhatsApp). The conversation is a real, tenant
 * scoped lead, so it also shows up in the CRM inbox for the selected agency.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import type { Me, Message, Tenant } from './types';

/** Human-readable label for each answer's source layer (for debugging/QA). */
const SOURCE_LABELS: Record<string, string> = {
  greeting: 'פתיח',
  prompt: 'שאלת משפך',
  re_prompt: 'חזרה על השאלה',
  correction: 'תיקון · חזרה לשלב',
  reply_bank_direct: 'בנק תשובות · התאמה ישירה',
  reply_bank_infer: 'בנק תשובות · הסקה',
  rag_answer: 'מאגר ידע (FAQ)',
  rag_no_answer: 'אין תשובה במאגר',
  rag_error: 'שגיאת מאגר',
  validation_error: 'בדיקת תקינות',
  age_ack: 'גיל · אושר',
  age_reprompt: 'גיל · בקשת מספר',
  confirm_unclear: 'אישור · לא ברור',
  vehicle_details: 'משרד התחבורה · פרטי רכב',
  vehicle_not_found: 'משרד התחבורה · לא נמצא',
  vehicle_lookup_error: 'משרד התחבורה · שגיאה',
  not_insurable: 'חסימת גיל',
  abuse: 'טיפול בפנייה פוגענית',
  handoff: 'העברה לנציג',
  quote: 'הצעת מחיר',
  quote_error: 'שגיאת הצעת מחיר',
  bot_reply: 'כללי',
};

const isRtl = (s: string) => /[\u0590-\u05FF]/.test(s || '');

/** A stable per-browser "phone number" so the bot treats you as one customer. */
function getStoredPhone(): string {
  let p = localStorage.getItem('simChatPhone');
  if (!p) {
    p = '972' + Math.floor(500000000 + Math.random() * 400000000);
    localStorage.setItem('simChatPhone', p);
  }
  return p;
}

interface EchoMessage extends Message {
  optimistic?: boolean;
}

export default function ChatSimulator() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [phone, setPhone] = useState<string>(() => getStoredPhone());
  const [messages, setMessages] = useState<EchoMessage[]>([]);
  const [input, setInput] = useState('');
  const messagesRef = useRef<HTMLDivElement | null>(null);

  // Resolve the caller. SystemAdmin always has access; other roles only where the
  // server opens the simulator to everyone (sandbox). Otherwise bounce back to the
  // CRM (the server also enforces the role on /api/dev).
  useEffect(() => {
    api
      .me()
      .then((u) => {
        const canUse = u.role === 'SystemAdmin' || !!u.chatSimulator?.allRoles;
        if (!canUse) {
          navigate('/', { replace: true });
          return;
        }
        setMe(u);
      })
      .catch((e) => setAuthError(String(e)));
  }, [navigate]);

  // SystemAdmin picks which agency's bot to drive; agency-scoped roles are locked
  // to their own tenant (and can't list other agencies).
  useEffect(() => {
    if (!me) return;
    if (me.role !== 'SystemAdmin') {
      setTenantId(me.tenantId);
      return;
    }
    api
      .listTenants()
      .then((t) => {
        setTenants(t);
        setTenantId((cur) => cur ?? t[0]?.tenant_id);
      })
      .catch(() => undefined);
  }, [me]);

  const isSystemAdmin = me?.role === 'SystemAdmin';

  const scrollToBottom = useCallback(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const poll = useCallback(async () => {
    if (!tenantId) return;
    try {
      const hist = await api.devMessages(phone, tenantId);
      setMessages((prev) => {
        // Keep an optimistic echo visible only until the server has recorded it.
        const pending = prev.filter(
          (m) => m.optimistic && !hist.some((h) => h.direction === 'inbound' && h.text === m.text),
        );
        return [...hist, ...pending];
      });
    } catch {
      /* ignore transient errors */
    }
  }, [phone, tenantId]);

  // Poll the conversation while a tenant is selected.
  useEffect(() => {
    if (!tenantId) return;
    void poll();
    const id = setInterval(() => void poll(), 1500);
    return () => clearInterval(id);
  }, [poll, tenantId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !tenantId) return;
    setInput('');
    // Optimistic echo so typing feels instant; poll reconciles against the log.
    setMessages((prev) => [
      ...prev,
      { phone, ts: new Date().toISOString(), direction: 'inbound', text, optimistic: true },
    ]);
    try {
      await api.devSendMessage(phone, text, tenantId);
    } catch (e) {
      alert('שליחה נכשלה: ' + (e as Error).message);
    }
    setTimeout(() => void poll(), 400);
    setTimeout(() => void poll(), 1200);
  }, [input, phone, tenantId, poll]);

  const resetConversation = useCallback(() => {
    localStorage.removeItem('simChatPhone');
    setPhone(getStoredPhone());
    setMessages([]);
  }, []);

  const activeTenantName = useMemo(
    () => tenants.find((t) => t.tenant_id === tenantId)?.name,
    [tenants, tenantId],
  );

  if (authError) return <div className="empty">שגיאה: {authError}</div>;
  if (!me) return <div className="empty">טוען…</div>;

  return (
    <div className="sim-root">
      <style>{SIM_STYLES}</style>

      <div className="sim-toolbar">
        <button className="sim-back" onClick={() => navigate('/')}>
          → חזרה למערכת
        </button>
        <div className="sim-toolbar-title">סימולטור צ'אט לקוח</div>
        <div className="sim-tenant">
          <span className="sim-tenant-label">סוכנות</span>
          {isSystemAdmin ? (
            <select
              className="sim-tenant-select"
              value={tenantId ?? ''}
              onChange={(e) => {
                setTenantId(e.target.value);
                setMessages([]);
              }}
            >
              {/* Default (system) tenant: the only one for which the sandbox lets
                  the bot use the shared Qualla account, so it's the way to test
                  live pricing here. Real agencies use their own Qualla secret. */}
              <option value="default">מערכת · ברירת מחדל (default)</option>
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.name} ({t.tenant_id})
                </option>
              ))}
            </select>
          ) : (
            <span className="sim-tenant-select" style={{ pointerEvents: 'none' }}>
              {tenantId}
            </span>
          )}
        </div>
      </div>

      <div className="sim-stage">
        <div className="sim-phone">
          <header className="sim-header">
            <div className="sim-avatar">{(activeTenantName || 'L').slice(0, 1)}</div>
            <div className="sim-who">
              <div className="sim-name">{activeTenantName || 'הבוט'}</div>
              <div className="sim-sub">מקוון · {phone}</div>
            </div>
            <button className="sim-reset" onClick={resetConversation}>
              שיחה חדשה
            </button>
          </header>

          <div className="sim-messages" ref={messagesRef}>
            {messages.map((m, i) => (
              <div key={`${m.ts}-${i}`} className="sim-row">
                <div
                  className={
                    'sim-msg ' +
                    m.direction +
                    (isRtl(m.text) ? '' : ' ltr')
                  }
                >
                  {m.text}
                  {m.ts && !m.optimistic && (
                    <div className="sim-time">{m.ts.slice(11, 16)}</div>
                  )}
                </div>
                {m.direction === 'outbound' && m.intent && (
                  <div className="sim-tag">
                    מקור: <b>{SOURCE_LABELS[m.intent] || m.intent}</b>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="sim-composer">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void send();
              }}
              placeholder="הקלד/י הודעה..."
              autoComplete="off"
            />
            <button title="שלח" onClick={() => void send()}>
              &#10148;
            </button>
          </div>
        </div>
        <div className="sim-note">
          מצב סימולציה: הבוט מעבד את השיחה ומתעד את התשובות, אך אינו שולח הודעות WhatsApp אמיתיות.
          השיחה מופיעה גם ב-CRM עבור הסוכנות הנבחרת.
        </div>
      </div>
    </div>
  );
}

const SIM_STYLES = `
.sim-root {
  --sim-bg: #0b141a;
  --sim-panel: #111b21;
  --sim-bar: #202c33;
  --sim-in: #202c33;
  --sim-out: #005c4b;
  --sim-text: #e9edef;
  --sim-muted: #8696a0;
  --sim-accent: #00a884;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0f13;
  color: var(--sim-text);
}
.sim-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  background: #0e181e;
  border-bottom: 1px solid #223;
}
.sim-toolbar-title { font-weight: 600; }
.sim-back {
  background: transparent;
  border: 1px solid #2a3942;
  color: var(--sim-text);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
}
.sim-tenant { margin-inline-start: auto; display: flex; align-items: center; gap: 8px; }
.sim-tenant-label { font-size: 12px; color: var(--sim-muted); }
.sim-tenant-select {
  background: var(--sim-panel);
  color: var(--sim-text);
  border: 1px solid #2a3942;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
}
.sim-stage {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
}
.sim-phone {
  width: 420px;
  max-width: 100%;
  height: 100%;
  max-height: 760px;
  display: flex;
  flex-direction: column;
  background: var(--sim-bg);
  border: 1px solid #223;
  border-radius: 10px;
  overflow: hidden;
}
.sim-header {
  background: var(--sim-bar);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.sim-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: var(--sim-accent); color: #04231c;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700;
}
.sim-who { flex: 1; }
.sim-who .sim-name { font-weight: 600; }
.sim-who .sim-sub { font-size: 12px; color: var(--sim-muted); }
.sim-reset { background: transparent; border: 0; color: var(--sim-muted); cursor: pointer; font-size: 12px; }
.sim-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: linear-gradient(rgba(11,20,26,0.94), rgba(11,20,26,0.94));
}
.sim-row { display: flex; flex-direction: column; }
.sim-msg {
  max-width: 78%;
  padding: 7px 11px;
  border-radius: 8px;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.35;
  font-size: 14.5px;
  direction: rtl;
  text-align: right;
}
.sim-msg.ltr { direction: ltr; text-align: left; }
.sim-msg.inbound { background: var(--sim-out); align-self: flex-end; border-top-right-radius: 2px; }
.sim-msg.outbound { background: var(--sim-in); align-self: flex-start; border-top-left-radius: 2px; }
.sim-msg .sim-time { font-size: 10px; color: var(--sim-muted); margin-top: 3px; }
.sim-tag {
  align-self: flex-start;
  margin: 2px 0 4px 0;
  font-size: 10px;
  color: var(--sim-muted);
  background: #0e181e;
  border: 1px solid #2a3942;
  border-radius: 10px;
  padding: 1px 8px;
  direction: rtl;
}
.sim-tag b { color: #cfe8df; font-weight: 600; }
.sim-composer { display: flex; gap: 8px; padding: 10px; background: var(--sim-bar); }
.sim-composer input {
  flex: 1; background: var(--sim-panel); border: 0; color: var(--sim-text);
  padding: 11px 14px; border-radius: 20px; outline: none; font-size: 14.5px;
}
.sim-composer button {
  background: var(--sim-accent); color: #04231c; border: 0;
  width: 44px; height: 44px; border-radius: 50%; cursor: pointer; font-size: 18px;
}
.sim-note {
  max-width: 420px;
  font-size: 12px;
  color: var(--sim-muted);
  text-align: center;
  line-height: 1.5;
}
`;
